import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { Search, Send } from "lucide-react";
import { toast } from "sonner";
import {
    Avatar,
    AvatarGradientFallback,
    Button,
    Checkbox,
    Container,
    Input,
    Mono,
    SectionHeader,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
    Text,
} from "@athena/ui";
import { cn } from "@athena/ui/utils";
import { getInitialsFrom } from "../../../global/formatHelpers";
import { OrganizationSelect } from "@/components/OrganizationSelect";
import {
    PushNotificationHistoryDocument,
    PushNotificationRecipientsDocument,
    SendPushNotificationDocument,
    type PushNotificationRecipientsQuery,
} from "@/graphql/pushNotifications.generated";
import { useAllRows } from "@/hooks/useAllRows";
import { PushNotificationComposer } from "@/components/pushNotifications/PushNotificationComposer";
import { PushNotificationHistory } from "@/components/pushNotifications/PushNotificationHistory";
import {
    draftMessage,
    EMPTY_DRAFT,
    isDraftReady,
    type PushNotificationDraft,
} from "@/components/pushNotifications/pushNotificationDraft";

/**
 * Everyone a push can reach, filtered by the server and read in full.
 *
 * The page used to fetch the newest 200 users and keep the ones with a token, so "Reachable" was a
 * count of the newest 200 (82 of roughly a thousand, ATH-162) and nobody older could be selected.
 * The set is every user with a registered device, small enough to hold whole, so `useAllRows`
 * pages through it and select-all means all.
 */
const RECIPIENT_FILTER = { filter: { hasPushToken: true } };

/* ─────────────────────────────── page ───────────────────────────────── */

export function PushNotificationsPage() {
    const [
        activeTab,
        setActiveTab,
    ] = useState("send");
    const [
        draft,
        setDraft,
    ] = useState<PushNotificationDraft>(EMPTY_DRAFT);
    const [
        org,
        setOrg,
    ] = useState("all");
    const [
        recipientSearch,
        setRecipientSearch,
    ] = useState<string>("");
    const [
        selected,
        setSelected,
    ] = useState<Set<string>>(new Set());

    const { rows: reachableUsers, loading: recipientsLoading } = useAllRows<
        PushNotificationRecipientsQuery["users"][number]
    >(PushNotificationRecipientsDocument, (data) => (data as PushNotificationRecipientsQuery | undefined)?.users, {
        variables: RECIPIENT_FILTER,
    });
    const {
        data: historyData,
        loading: historyLoading,
        refetch: refetchHistory,
    } = useQuery(PushNotificationHistoryDocument);
    const [
        sendPushNotification,
        { loading: sending },
    ] = useMutation(SendPushNotificationDocument);

    const TOTAL_REACHABLE = reachableUsers.length;
    const SENT_COUNT = historyData?.pushNotificationHistory.length ?? 0;

    const recipients = reachableUsers.map((u) => ({
        id: u.id,
        name: `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim() || u.email,
        initials: getInitialsFrom(u.firstName ?? "", u.lastName ?? ""),
        org: u.memberships[0]?.organization.name ?? "—",
        // Every org they belong to, so filtering by their second org still finds them.
        orgIds: u.memberships.map((m) => m.organization.id),
        email: u.email,
    }));

    const filteredRecipients = recipients.filter((r) => {
        if (org !== "all" && !r.orgIds.includes(org)) return false;
        const q = recipientSearch.toLowerCase();
        return r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q) || r.org.toLowerCase().includes(q);
    });

    const allSelected = filteredRecipients.length > 0 && filteredRecipients.every((r) => selected.has(r.id));
    const someSelected = filteredRecipients.some((r) => selected.has(r.id)) && !allSelected;

    function toggleAll() {
        if (allSelected) {
            setSelected((prev) => {
                const next = new Set(prev);
                filteredRecipients.forEach((r) => next.delete(r.id));
                return next;
            });
        } else {
            setSelected((prev) => {
                const next = new Set(prev);
                filteredRecipients.forEach((r) => next.add(r.id));
                return next;
            });
        }
    }

    function toggleOne(id: string) {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    }

    async function handleSend() {
        try {
            await sendPushNotification({
                variables: {
                    input: {
                        ...draftMessage(draft),
                        userIds: Array.from(selected),
                    },
                },
            });
            toast.success("Push notification sent");
            setDraft(EMPTY_DRAFT);
            setSelected(new Set());
            await refetchHistory();
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Failed to send push notification");
        }
    }

    return (
        <Container className="p-6">
            <SectionHeader
                eyebrow={`Engagement · ${SENT_COUNT} Sent · ${recipientsLoading ? "…" : TOTAL_REACHABLE.toLocaleString()} Reachable`}
                title={"Push Notifications"}
                description={"Send push notifications to users who have enabled notifications in the mobile app."}
            />

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList variant="line" className="border-border mb-6 border-b">
                    <TabsTrigger
                        variant="line"
                        value="send"
                        className="data-[state=active]:border-secondary data-[state=active]:text-secondary"
                    >
                        Send notification
                    </TabsTrigger>
                    <TabsTrigger
                        variant="line"
                        value="history"
                        className="data-[state=active]:border-secondary data-[state=active]:text-secondary"
                    >
                        Notification history ({SENT_COUNT})
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="send" className="mt-0 space-y-4">
                    <PushNotificationComposer draft={draft} onChange={setDraft} showDeepLinkConfig />

                    {/* Filter by org section */}
                    <div className="border-border rounded-2xl border">
                        <div className="bg-card-background border-border rounded-t-2xl border-b px-5 py-3">
                            <Mono className="text-muted-foreground text-xs tracking-widest uppercase">
                                Filter by Organization
                            </Mono>
                        </div>
                        <div className="px-5 py-4">
                            <OrganizationSelect
                                value={org}
                                onValueChange={setOrg}
                                className="w-full"
                                aria-label="Filter by organization"
                                leadingOptions={[{ value: "all", label: "All organizations" }]}
                            />
                        </div>
                    </div>

                    {/* Recipients section */}
                    <div className="border-border rounded-2xl border">
                        <div className="bg-card-background border-border rounded-t-2xl border-b px-5 py-3">
                            <Mono className="text-muted-foreground text-xs tracking-widest uppercase">Recipients</Mono>
                        </div>
                        <div className="px-5 py-4">
                            {/* Search + selected count */}
                            <div className="mb-3 flex items-center gap-3">
                                <div className="relative flex-1">
                                    <Search className="text-muted-foreground absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2" />
                                    <Input
                                        placeholder="Search recipients by name, email, or organization..."
                                        className="h-9 pl-9 text-sm"
                                        value={recipientSearch}
                                        onChange={(e) => setRecipientSearch(e.target.value)}
                                    />
                                </div>
                                <Mono className="text-muted-foreground shrink-0 text-xs">
                                    {selected.size} of {TOTAL_REACHABLE.toLocaleString()} selected
                                </Mono>
                            </div>

                            {/* Recipient table */}
                            <div className="border-border rounded-xl border">
                                {/* Table header */}
                                <div className="border-border bg-muted/40 flex items-center gap-4 rounded-t-xl border-b px-4 py-2.5">
                                    <Checkbox
                                        checked={allSelected}
                                        data-state={
                                            someSelected ? "indeterminate" : allSelected ? "checked" : "unchecked"
                                        }
                                        onCheckedChange={toggleAll}
                                        aria-label="Select all"
                                        className={someSelected ? "opacity-60" : ""}
                                    />
                                    <Mono className="text-muted-foreground flex-1 text-[11px] tracking-widest uppercase">
                                        Name
                                    </Mono>
                                    <Mono className="text-muted-foreground w-44 text-[11px] tracking-widest uppercase">
                                        Organization
                                    </Mono>
                                    <Mono className="text-muted-foreground w-52 text-[11px] tracking-widest uppercase">
                                        Email
                                    </Mono>
                                </div>

                                {/* Rows */}
                                <div className="divide-border divide-y">
                                    {recipientsLoading && (
                                        <Text muted className="px-4 py-3 text-sm">
                                            Loading recipients…
                                        </Text>
                                    )}
                                    {!recipientsLoading && filteredRecipients.length === 0 && (
                                        <Text muted className="px-4 py-3 text-sm">
                                            No recipients with notifications enabled match this filter.
                                        </Text>
                                    )}
                                    {filteredRecipients.map((r) => (
                                        <div
                                            key={r.id}
                                            onClick={() => toggleOne(r.id)}
                                            className={cn(
                                                "flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors",
                                                selected.has(r.id) ? "bg-secondary/5" : "hover:bg-muted/30",
                                            )}
                                        >
                                            <Checkbox
                                                checked={selected.has(r.id)}
                                                onCheckedChange={() => toggleOne(r.id)}
                                                onClick={(e) => e.stopPropagation()}
                                                aria-label={`Select ${r.name}`}
                                            />
                                            <div className="flex flex-1 items-center gap-2.5">
                                                <Avatar size="xs" className="shrink-0">
                                                    <AvatarGradientFallback>{r.initials}</AvatarGradientFallback>
                                                </Avatar>
                                                <Text className="text-sm">{r.name}</Text>
                                            </div>
                                            <Text muted className="w-44 truncate text-sm">
                                                {r.org}
                                            </Text>
                                            <Mono muted className="w-52 truncate text-xs">
                                                {r.email}
                                            </Mono>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="history" className="mt-0">
                    <PushNotificationHistory
                        notifications={historyData?.pushNotificationHistory ?? []}
                        loading={historyLoading}
                    />
                </TabsContent>
            </Tabs>

            {/* Sticky footer */}
            {activeTab === "send" && (
                <div className="border-border bg-background sticky bottom-0">
                    <div className="flex items-center justify-between px-6 py-4">
                        <Text muted className="text-sm">
                            Sends immediately — {selected.size} recipient
                            {selected.size !== 1 ? "s" : ""} with notifications enabled.
                        </Text>
                        <Button
                            size="default"
                            variant="default"
                            className="gap-2"
                            disabled={selected.size === 0 || !isDraftReady(draft) || sending}
                            onClick={() => void handleSend()}
                        >
                            <Send className="h-4 w-4" />
                            {sending ? "Sending…" : "Send notification"}
                        </Button>
                    </div>
                </div>
            )}
        </Container>
    );
}
